import { useEffect, useState, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import GenerationProgress from '../components/GenerationProgress';
import RecapDisplay from '../components/RecapDisplay';
import EpisodeProgressDots from '../components/EpisodeProgressDots';
import gameApi from '../services/GameApiService';
import type { EpisodeSummary, NPCReaction } from '../services/GameApiService';
import avatarPlaceholderMale from '../assets/avatar-placeholder-male.png';
import avatarPlaceholderFemale from '../assets/avatar-placeholder-female.png';
import avatarPlaceholderUnknown from '../assets/avatar-placeholder-unknown.png';
import { saveSession } from '../utils/sessionPersistence';
import { saveDeviceToken } from '../utils/deviceSession';
import HandsFreeToggle from '../components/HandsFreeToggle';
import { useWaitingVoice, type WaitingVoiceContext } from '../voice/useWaitingVoice';
import { useHandsFree } from '../voice/HandsFreeProvider';

interface WaitingData {
  status?: 'completed'; // Set when adventure is completed
  nextEpisodeStatus:
    | 'pending'
    | 'processing_recap'
    | 'preparing_candidates'
    | 'candidates_ready'
    | 'preparing_selected_episode'
    | 'episode_ready'
    // Legacy states kept for backwards compatibility during rollout
    | 'generating'
    | 'ready';
  nextEpisodeMode?: 'standard' | 'resolve';
  scenarioReady?: boolean;
  /** HISTÓRIA bridge — written early (~5-15s after episode end). Frontend locks
   *  this once shown so a later re-generation inside `scenarioData.frontendSituation`
   *  can never replace the text the reader is already looking at. */
  nextBridgeText?: string | null;
  /** Bridge text into the next encounter, appended to the recap so the two read as one chapter. */
  nextFrontendSituation?: string | null;
  lastEpisode?: {
    characterName: string;
    summary: EpisodeSummary;
    npcReaction: NPCReaction;
  };
  pendingReaction?: {
    characterName: string;
    npcReaction: NPCReaction;
  } | null;
  pendingEpisodeSummary?: EpisodeSummary | null;
  nextCandidates?: Array<{
    id: string;
    name: string;
    role: string;
    hook: string;
    reasonSafe: string;
    referenceSource: string;
    isKnownCharacter: boolean;
    genderHint?: 'male' | 'female' | 'unknown';
    interactionCount: number;
    interactionLimit: number;
    openQuestions?: string[];
  }>;
  selectedCandidateId?: string | null;
  allowResolveEarly: boolean;
  estimatedReadyAt?: string;
  generationStep?: number | null;
  generationStepName?: string | null;
  generationError?: string | null;
  adventureTitle: string;
  currentEpisode: number;
  totalEpisodes?: number;
  isLastEpisode?: boolean;
  initialCharacterAvatarUrl?: string | null;
  completedEpisodesForDots?: Array<{
    episodeNumber: number;
    characterName: string;
    avatarUrl: string | null;
  }>;
  /** true quando o playerState já tem email (não pedir de novo). */
  hasEmail?: boolean;
}

// Episódio 1 = briefing da autoridade; episódio 2 = primeira conversa.
// O email só se pede depois de o jogador estar investido (fim do EP2).
const EMAIL_PROMPT_AFTER_EPISODE = 2;
// sessionStorage flag para o "Agora não" — não persiste entre sessões de browser.
const EMAIL_PROMPT_DISMISSED_KEY = 'selna_email_prompt_dismissed';

export default function WaitingPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Immediate episode data passed from EpisodePage (available before async job completes)
  const immediateState = location.state as {
    episodeSummary?: EpisodeSummary;
    characterName?: string;
    episodeNumber?: number;
    isLastEpisode?: boolean;
    isResolveEpisodeCompleted?: boolean;
    totalEpisodes?: number;
    avatarUrl?: string | null;
  } | null;

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState<WaitingData | null>(null);
  const [pollCount, setPollCount] = useState(0);
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(null);
  const [choiceError, setChoiceError] = useState('');
  const [isSelectingCandidate, setIsSelectingCandidate] = useState(false);
  const [resolveError, setResolveError] = useState('');
  const [isResolving, setIsResolving] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 3;

  // ── Email prompt (F4) ─────────────────────────────────────────────────
  // Pede-se o email no fim do EP2 para sincronização cross-device. O cartão
  // aparece quando o jogador já completou o EP2 (displayEpisodeNumber >= 2),
  // o job assíncrono terminou (currentEpisode > displayEpisodeNumber) e ainda
  // não tem email. "Agora não" grava um flag em sessionStorage (não persiste).
  const [emailPromptEmail, setEmailPromptEmail] = useState('');
  const [emailPromptError, setEmailPromptError] = useState('');
  const [isAttachingEmail, setIsAttachingEmail] = useState(false);
  const [emailAttached, setEmailAttached] = useState(false);
  const [emailPromptDismissed, setEmailPromptDismissed] = useState(
    () => sessionStorage.getItem(EMAIL_PROMPT_DISMISSED_KEY) === '1',
  );
  // Conflito: o email já tem um run noutro device. O modal (F5) pede ao
  // jogador qual run manter.
  const [conflictData, setConflictData] = useState<{
    existing: { adventureTitle: string; currentEpisode: number; totalEpisodes: number; playerStatus: string };
    current: { adventureTitle: string; currentEpisode: number; totalEpisodes: number };
  } | null>(null);
  const [conflictEmail, setConflictEmail] = useState('');
  const [isResolvingConflict, setIsResolvingConflict] = useState(false);
  const [conflictError, setConflictError] = useState('');

  // 🚗 Hands-free voice navigation — enabled lives in the app-level provider.
  const { enabled: handsFree, setEnabled: handleHandsFreeChange } = useHandsFree();

  // ── Stable recap display ──────────────────────────────────────────────
  // Summary comes from polling (pendingEpisodeSummary, available after Steps 1+2),
  // or from the last completed episode. Once set, never replaced WITHIN the same
  // navigation. When the user returns from a NEW episode, location.key changes
  // and we reinitialize all recap state.
  const [lockedSummary, setLockedSummary] = useState<EpisodeSummary | null>(
    immediateState?.episodeSummary || null,
  );
  // The bridge text (the inter-episode interval prose) is shown on the waiting
  // page appended to the recap, so recap + bridge read as one continuous
  // chapter while the next scenario builds. It arrives via polling as
  // nextBridgeText (frozen early by generateEpisodeBridge, ~5-15s after the
  // episode ends). Once shown, it is locked — a later re-generation inside
  // scenarioData.frontendSituation can never replace what the reader is
  // already looking at. See plans/fix-bugB-bridge-text-exploracao-whodunnit.md.
  const [lockedBridge, setLockedBridge] = useState<string | null>(null);
  const [displayCharacterName, setDisplayCharacterName] = useState<string>(
    immediateState?.characterName || '',
  );
  const [displayEpisodeNumber, setDisplayEpisodeNumber] = useState<number | undefined>(
    immediateState?.episodeNumber,
  );
  const displaySummary: EpisodeSummary | null = lockedSummary;
  
  const isResolveEpisode = data?.nextEpisodeMode === 'resolve';
  const isResolveFlow = isResolveEpisode || immediateState?.isResolveEpisodeCompleted === true;
  const isCandidatesReady = data?.nextEpisodeStatus === 'candidates_ready' || data?.nextEpisodeStatus === 'ready';
  const isProcessingRecap = data?.nextEpisodeStatus === 'processing_recap';
  const isPreparingCandidates = data?.nextEpisodeStatus === 'preparing_candidates'
    || data?.nextEpisodeStatus === 'generating';
  const isPreparingSelectedEpisode = data?.nextEpisodeStatus === 'preparing_selected_episode';
  const isEpisodeReady = data?.nextEpisodeStatus === 'episode_ready';
  // NOTA: o scroll para o topo NÃO deve ser automático em cada mudança de fase.
  // Anteriormente usávamos useScrollToTopOnPhaseChange(data?.nextEpisodeStatus),
  // o que fazia a página saltar para o topo quando o episódio ficava pronto
  // (transição automática via polling, sem input do utilizador). Agora o scroll
  // só acontece em ações iniciadas pelo utilizador (escolher personagem,
  // resolver caso) — ver handleConfirmCandidate / handleResolveCase. As
  // navegações para outras rotas continuam cobertas pelo <ScrollToTop> global.
  // Determine if this was the last numbered episode (from immediate state or polled data).
  // Do not infer "last episode" from resolve mode, because resolve can start early.
  // Safety: if polled data clearly shows the adventure continues (candidates ready),
  // override isLastEpisode to false. This prevents the UI from getting stuck showing
  // "A finalizar a aventura..." when the backend actually generated candidates.
  const isLastEpisode = (() => {
    const fromState = immediateState?.isLastEpisode || data?.isLastEpisode || false;
    if (fromState && isCandidatesReady && data?.nextCandidates && data.nextCandidates.length > 0) {
      return false;
    }
    return fromState;
  })();
  
  // Post-resolution: the resolve episode is DONE, backend is now processing final evaluation.
  // We know this because the episode-complete response told us the resolve episode was completed.
  // The backend still has nextEpisodeMode === 'resolve' while processing the final evaluation.
  const isProcessingFinalEvaluation = immediateState?.isResolveEpisodeCompleted === true && (isProcessingRecap || isPreparingSelectedEpisode);
  
  // Track when the adventure is completed (final evaluation done) but user hasn't clicked the button yet
  const [evaluationReady, setEvaluationReady] = useState(false);

  const shouldShowStatusIndicator = !evaluationReady && (
    isProcessingRecap
    || isPreparingCandidates
    || isPreparingSelectedEpisode
    || isProcessingFinalEvaluation
    || (!isResolveEpisode && isLastEpisode)
  );
  
  // NPC reaction is the only thing that gets updated (added, not replaced)
  const [displayNpcReaction, setDisplayNpcReaction] = useState<NPCReaction | undefined>(
    undefined,
  );
  const hasReceivedReactionRef = useRef(false);

  // ── Reset ALL recap state when a new navigation occurs ────────────────
  // React Router reuses the component when navigating to the same route path.
  // location.key is unique per navigation entry, so when it changes we know
  // the user arrived from a new episode completion, and all stale state must go.
  const prevLocationKeyRef = useRef(location.key);
  useEffect(() => {
    if (location.key === prevLocationKeyRef.current) return;
    prevLocationKeyRef.current = location.key;

    // Reinitialize recap state from the fresh immediateState
    setLockedSummary(immediateState?.episodeSummary || null);
    setLockedBridge(null);
    setDisplayCharacterName(immediateState?.characterName || '');
    setDisplayEpisodeNumber(immediateState?.episodeNumber);
    setDisplayNpcReaction(undefined);
    hasReceivedReactionRef.current = false;

    // Reset page-level state for the new waiting cycle
    setData(null);
    setIsLoading(true);
    setError('');
    setSelectedCandidateId(null);
    setChoiceError('');
    setEvaluationReady(false);
    setRetryCount(0);

    loadWaitingStatus();
  }, [location.key]);

  useEffect(() => {
    if (!token) {
      navigate('/');
      return;
    }

    loadWaitingStatus();
  }, [token]);

  // Poll for status updates
  useEffect(() => {
    if (!token || !data) return;

    // If adventure is completed, show the "Ver avaliação" button instead of auto-redirecting.
    // This prevents interrupting the user while they are reading the episode recap.
    if (data.status === 'completed') {
      setEvaluationReady(true);
      return;
    }

    // When the episode is ready, do NOT auto-navigate — the user may still be reading.
    // The UI will show a "next episode ready" button instead.

    // Poll while content is still being prepared.
    // Also poll when isLastEpisode is true — the backend is generating the final evaluation
    // and we need to keep polling until status becomes 'completed'.
    const shouldPoll = isProcessingRecap || isPreparingCandidates || isPreparingSelectedEpisode
      || (isLastEpisode && !evaluationReady);
    if (shouldPoll) {
      let interval: NodeJS.Timeout | null = null;
      let isPageVisible = !document.hidden;

      const startPolling = () => {
        if (interval) return;
        // Performance: poll fast (3s) while the recap is still being generated so
        // the user sees the recap text appear as soon as the backend writes it to
        // Firestore. For the rest of the pipeline (candidates, episode prep,
        // final evaluation), status changes are less time-sensitive and 10s is
        // fine — the useEffect's dep on `isProcessingRecap` restarts the polling
        // automatically when we cross the threshold, so the interval always
        // matches the current phase.
        const pollIntervalMs = isProcessingRecap ? 3000 : 10000;
        interval = setInterval(() => {
          // Only poll if page is visible
          if (!document.hidden) {
            loadWaitingStatus();
            setPollCount(c => c + 1);
          }
        }, pollIntervalMs);
      };

      const stopPolling = () => {
        if (interval) {
          clearInterval(interval);
          interval = null;
        }
      };

      const handleVisibilityChange = () => {
        const wasVisible = isPageVisible;
        isPageVisible = !document.hidden;

        if (!wasVisible && isPageVisible) {
          // Page became visible - refresh immediately
          loadWaitingStatus();
          setPollCount(c => c + 1);
          startPolling();
        } else if (wasVisible && !isPageVisible) {
          // Page became hidden - stop polling
          stopPolling();
        }
      };

      // Listen for visibility changes
      document.addEventListener('visibilitychange', handleVisibilityChange);

      // Start polling if page is currently visible
      if (isPageVisible) {
        startPolling();
      }

      return () => {
        stopPolling();
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }
  }, [data, token, isEpisodeReady, isResolveEpisode, isProcessingRecap, isPreparingCandidates, isPreparingSelectedEpisode, isLastEpisode, evaluationReady]);

  // Update NPC reaction as soon as it is available (once).
  // Priority:
  // 1) pendingReaction => available during async processing right after Step 1
  // 2) lastEpisode.npcReaction => available after async job completes and episode is persisted
  useEffect(() => {
    if (hasReceivedReactionRef.current) return;

    const pendingReaction = data?.pendingReaction;
    if (pendingReaction?.npcReaction) {
      const matchesCurrentRecap = !displayCharacterName || pendingReaction.characterName === displayCharacterName;
      if (matchesCurrentRecap) {
        hasReceivedReactionRef.current = true;
        setDisplayNpcReaction(pendingReaction.npcReaction);
        return;
      }
    }

    if (!data?.lastEpisode?.npcReaction) return;

    // Only accept lastEpisode when backend has completed this episode.
    // currentEpisode increments to (displayEpisodeNumber + 1) at the end of async processing.
    const asyncJobDone = !displayEpisodeNumber || data.currentEpisode > displayEpisodeNumber;
    if (asyncJobDone) {
      hasReceivedReactionRef.current = true;
      setDisplayNpcReaction(data.lastEpisode.npcReaction);
    }
  }, [
    data?.pendingReaction,
    data?.lastEpisode?.npcReaction,
    data?.currentEpisode,
    displayEpisodeNumber,
    displayCharacterName,
  ]);

  // Lock episode summary as soon as it arrives from polling (once).
  useEffect(() => {
    if (lockedSummary) return;

    if (data?.pendingEpisodeSummary) {
      setLockedSummary(data.pendingEpisodeSummary);
      return;
    }

    if (!data?.lastEpisode?.summary) return;
    const asyncJobDone = !displayEpisodeNumber || data.currentEpisode > displayEpisodeNumber;
    if (asyncJobDone) {
      setLockedSummary(data.lastEpisode.summary);
    }
  }, [data?.pendingEpisodeSummary, data?.lastEpisode?.summary, data?.currentEpisode, displayEpisodeNumber, lockedSummary]);

  // Lock the bridge text as soon as it arrives from polling (once). The bridge
  // is frozen early by generateEpisodeBridge (~5-15s after episode end) and
  // arrives as nextBridgeText. Once locked, a later frontendSituation (from
  // the scenario build) can never replace it — the reader is already looking
  // at it. See plans/fix-bugB-bridge-text-exploracao-whodunnit.md.
  useEffect(() => {
    if (lockedBridge) return;
    if (data?.nextBridgeText && data.nextBridgeText.trim() !== '') {
      setLockedBridge(data.nextBridgeText);
    }
  }, [data?.nextBridgeText, lockedBridge]);

  const loadWaitingStatus = async () => {
    if (!token) return;

    try {
      const waitingData = await gameApi.getWaitingStatus(token);
      if (!waitingData.adventureTitle) {
        throw new Error('Faltam dados da aventura');
      }
      if (typeof waitingData.totalEpisodes !== 'number' || waitingData.totalEpisodes < 1) {
        throw new Error('Faltam dados do total de episódios');
      }
      if (typeof waitingData.allowResolveEarly !== 'boolean') {
        throw new Error('Faltam dados de resolução antecipada');
      }
      if (typeof waitingData.currentEpisode !== 'number') {
        throw new Error('Faltam dados do episódio atual');
      }
      setData(waitingData);
      setIsLoading(false);
      setRetryCount(0); // Reset retry count on success

      // Persist session so the player can return without the magic link
      saveSession({
        token: token!,
        adventureTitle: waitingData.adventureTitle,
        currentEpisode: waitingData.currentEpisode,
        totalEpisodes: waitingData.totalEpisodes,
        lastPage: 'waiting',
        playerStatus: waitingData.status === 'completed' ? 'completed' : 'active',
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load status';
      
      // If it's a "Failed to fetch" error and we haven't exceeded retries, try again
      if (errorMessage.includes('Failed to fetch') && retryCount < maxRetries) {
        console.log(`[WaitingPage] Network error, retry ${retryCount + 1}/${maxRetries}`);
        setRetryCount(retryCount + 1);
        // Retry after a delay (exponential backoff: 1s, 2s, 4s)
        setTimeout(() => {
          loadWaitingStatus();
        }, Math.pow(2, retryCount) * 1000);
      } else {
        // Either not a network error, or we've exceeded retries
        setError(errorMessage);
        setIsLoading(false);
      }
    }
  };

  const handleConfirmCandidate = async (idArg?: string) => {
    if (!token || !data) return;
    // Voice path passes the id directly to avoid reading stale selection state.
    const candidateId = idArg ?? selectedCandidateId;
    if (!candidateId) {
      setChoiceError('Escolhe um personagem para continuar');
      return;
    }

    setIsSelectingCandidate(true);
    setChoiceError('');

    // Ação iniciada pelo utilizador: rebobina o scroll para o topo para que o
    // recap/ecrã de geração apareça desde o início. (As transições automáticas
    // via polling NÃO fazem scroll — só o input do user o provoca.)
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });

    try {
      await gameApi.selectNextCandidate(token, candidateId);
      await loadWaitingStatus();
    } catch (err) {
      setChoiceError(err instanceof Error ? err.message : 'Falha ao escolher personagem');
    } finally {
      setIsSelectingCandidate(false);
    }
  };

  const getCandidateAvatar = (genderHint?: 'male' | 'female' | 'unknown') => {
    if (genderHint === 'male') {
      return avatarPlaceholderMale;
    }
    if (genderHint === 'female') {
      return avatarPlaceholderFemale;
    }
    return avatarPlaceholderUnknown;
  };

  const handleResolveCase = async () => {
    if (!token) return;

    setIsResolving(true);
    setResolveError('');

    // Ação iniciada pelo utilizador: rebobina o scroll para o topo.
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });

    try {
      await gameApi.resolveCase(token);
      await loadWaitingStatus();
    } catch (err) {
      setResolveError(err instanceof Error ? err.message : 'Falha ao resolver o caso');
    } finally {
      setIsResolving(false);
    }
  };

  // ── Email prompt handlers (F4/F5) ─────────────────────────────────────
  // Submeter o email → attachEmail (B3). attached → esconde o cartão e mostra
  // confirmação. conflict → abre o modal de escolha (F5).
  const handleAttachEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !emailPromptEmail.trim()) return;
    setIsAttachingEmail(true);
    setEmailPromptError('');
    try {
      const result = await gameApi.attachEmail(token, emailPromptEmail.trim());
      if (result.status === 'attached') {
        setEmailAttached(true);
      } else if (result.status === 'conflict' && result.existing && result.current) {
        setConflictEmail(emailPromptEmail.trim());
        setConflictData({
          existing: result.existing,
          current: result.current,
        });
      }
    } catch (err) {
      setEmailPromptError(err instanceof Error ? err.message : 'Falha ao anexar email');
    } finally {
      setIsAttachingEmail(false);
    }
  };

  const handleDismissEmailPrompt = () => {
    sessionStorage.setItem(EMAIL_PROMPT_DISMISSED_KEY, '1');
    setEmailPromptDismissed(true);
  };

  // Resolver conflito (B4). keep:'current' → fica neste run. keep:'existing'
  // → recebe token novo e navega para o run existente.
  const handleResolveConflict = async (keep: 'current' | 'existing') => {
    if (!token || !conflictEmail) return;
    setIsResolvingConflict(true);
    setConflictError('');
    try {
      const result = await gameApi.resolveEmailConflict(token, conflictEmail, keep);
      if (result.status === 'attached') {
        // keep:'current' — o run anónimo venceu. Fecha o modal, marca como anexado.
        setConflictData(null);
        setEmailAttached(true);
      } else if (result.status === 'switch' && result.token) {
        // keep:'existing' — navega para o run existente com o token novo.
        // Grava o token novo no cookie antes de navegar.
        saveDeviceToken(result.token);
        if (result.playerStatus === 'completed') {
          navigate(`/complete/${result.token}`);
        } else {
          navigate(`/play/${result.token}`);
        }
      }
    } catch (err) {
      setConflictError(err instanceof Error ? err.message : 'Falha ao resolver conflito');
    } finally {
      setIsResolvingConflict(false);
    }
  };

  // 🚗 Hands-free voice: derive the actionable phase from current (poll-driven) state.
  const allowResolveVoice = !!(
    data?.allowResolveEarly &&
    isCandidatesReady &&
    !isLastEpisode &&
    !isResolveEpisode &&
    data?.totalEpisodes &&
    data.currentEpisode > Math.ceil((data.totalEpisodes - 2) / 2) + 1
  );

  const voiceContext: WaitingVoiceContext = useMemo(() => {
    // Episode ready: read recap + "continuar" prompt
    if (isEpisodeReady && !evaluationReady) {
      return {
        phase: 'ready',
        isResolve: isResolveEpisode,
        recap: displaySummary
          ? { characterName: displayCharacterName, summary: displaySummary }
          : undefined,
        bridgeText: lockedBridge,
      };
    }
    // Recap available but episode still loading: read the recap NOW
    // (audio starts as soon as "O que aconteceu" text is visible)
    if (
      displaySummary &&
      isPreparingSelectedEpisode &&
      !isEpisodeReady
    ) {
      return {
        phase: 'recap',
        isResolve: isResolveEpisode,
        recap: { characterName: displayCharacterName, summary: displaySummary },
      };
    }
    if (
      isCandidatesReady &&
      !isLastEpisode &&
      !isResolveEpisode &&
      data?.nextCandidates &&
      data.nextCandidates.length > 0
    ) {
      const selectable = data.nextCandidates
        .filter((c: any) => !c.isExhausted)
        .map((c) => ({ id: c.id, name: c.name, role: c.role }));
      if (selectable.length > 0) {
        return { phase: 'choose', candidates: selectable, allowResolve: allowResolveVoice };
      }
    }
    return { phase: 'none' };
  }, [
    isEpisodeReady,
    evaluationReady,
    isResolveEpisode,
    displaySummary,
    displayCharacterName,
    isPreparingSelectedEpisode,
    isCandidatesReady,
    isLastEpisode,
    data?.nextCandidates,
    allowResolveVoice,
    lockedBridge,
  ]);

  useWaitingVoice({
    enabled: handsFree,
    token: token || '',
    context: voiceContext,
    onSelectCandidate: (id) => {
      setSelectedCandidateId(id);
      void handleConfirmCandidate(id);
    },
    onNextEpisode: () => navigate(`/play/${token}`),
    onResolveCase: () => {
      void handleResolveCase();
    },
  });

  if (isLoading) {
    return (
      <div className="dark min-h-screen flex items-center justify-center bg-dark">
        <LoadingSpinner size="lg" message="A carregar..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="dark min-h-screen flex items-center justify-center px-4 bg-dark">
        <div className="max-w-md w-full text-center">
          <div className="text-6xl mb-4">😕</div>
          <h1 className="text-2xl font-bold text-dark dark:text-white mb-4">
            Oops! Algo correu mal
          </h1>
          <p className="text-gray-600 dark:text-white/70 mb-6">{error}</p>
          <a
            href="/"
            className="inline-block px-6 py-3 bg-primary text-white rounded-xl hover:bg-opacity-90 transition-colors"
          >
            Voltar ao início
          </a>
        </div>
      </div>
    );
  }

  if (!data) return null;

  // Get total episodes from immediate state or polled data
  const displayTotalEpisodes = data.totalEpisodes ?? 0;

  return (
    <div className="dark min-h-screen py-8 px-4 bg-dark">
      <div className="max-w-2xl mx-auto">
        {/* Hands-free voice toggle */}
        <div className="flex justify-end mb-4">
          <HandsFreeToggle enabled={handsFree} onChange={handleHandsFreeChange} />
        </div>
        {/* Header */}
        <div className="text-center mb-8">
          <span className="text-sm font-medium text-primary uppercase tracking-wider">
            {data.adventureTitle}
          </span>
          {displayTotalEpisodes > 0 && displayEpisodeNumber && (
            <p className="text-sm text-gray-500 dark:text-white/70 mt-1">
              Episódio {displayEpisodeNumber} de {displayTotalEpisodes} concluído
            </p>
          )}
          <h1 className="text-3xl font-bold text-dark dark:text-white mt-2">
            {evaluationReady
              ? 'Avaliação final pronta!'
              : isProcessingFinalEvaluation
                ? 'A processar a avaliação final...'
                : isEpisodeReady && isResolveEpisode
                  ? 'Episódio de resolução pronto!'
                  : isEpisodeReady && !isLastEpisode
                    ? 'Próximo Episódio Pronto!'
                    : isCandidatesReady && !isLastEpisode && !isResolveEpisode
                      ? 'Escolhe a próxima entrevista'
                      : isPreparingSelectedEpisode && isResolveEpisode
                        ? 'A preparar o episódio de resolução...'
                        : isPreparingSelectedEpisode
                          ? 'A preparar o próximo episódio...'
                          : isPreparingCandidates && !isResolveEpisode
                            ? 'A escolher personagens para a próxima entrevista...'
                            : isProcessingRecap && isResolveEpisode
                              ? 'A preparar o episódio de resolução...'
                              : isProcessingRecap
                                ? 'A analisar a conversa anterior...'
                                : isResolveFlow
                                  ? 'A preparar o episódio de resolução...'
                                  : isLastEpisode
                                    ? 'A finalizar a aventura...'
                                    : 'A preparar o próximo episódio...'}
          </h1>
          {evaluationReady && (
            <p className="text-gray-500 dark:text-white/70 mt-2">
              Já podes ver os resultados da tua aventura.
            </p>
          )}
          {!evaluationReady && isProcessingFinalEvaluation && (
            <p className="text-gray-500 dark:text-white/70 mt-2">
              Estamos a analisar toda a tua aventura e a preparar a avaliação final.
            </p>
          )}
          {!evaluationReady && !isProcessingFinalEvaluation && isResolveEpisode && (isProcessingRecap || isPreparingSelectedEpisode) && (
            <p className="text-gray-500 dark:text-white/70 mt-2">
              A conversa de resolução vai começar assim que estiver pronta.
            </p>
          )}
          {!evaluationReady && !isProcessingFinalEvaluation && !isResolveEpisode && isLastEpisode && (
            <p className="text-gray-500 dark:text-white/70 mt-2">
              Episódio final concluído! A preparar a tua avaliação...
            </p>
          )}
          {/* Episode progress dots */}
          {displayTotalEpisodes > 0 && data.completedEpisodesForDots && (
            <EpisodeProgressDots
              totalEpisodes={displayTotalEpisodes}
              currentEpisode={data.currentEpisode}
              initialCharacterAvatarUrl={data.initialCharacterAvatarUrl ?? null}
              completedEpisodes={data.completedEpisodesForDots}
              ringOffsetClassName="ring-offset-[#faf8f5] dark:ring-offset-dark"
            />
          )}
          {/* No subtitle for processing_recap in normal flow — the status indicator below already explains */}
        </div>

        {/* "Next episode ready" button — sticky so it stays visible while reading the recap */}
        {isEpisodeReady && token && !evaluationReady && (
          <div className="sticky top-0 z-20 -mx-4 px-4 pt-2 pb-4 bg-gradient-to-b from-gray-50 dark:from-dark via-gray-50 dark:via-dark to-transparent">
            <div className="bg-white dark:bg-white/10 dark:backdrop-blur-md dark:border dark:border-white/15 rounded-2xl shadow-sm dark:shadow-none p-6 text-center">
              <p className="text-gray-600 dark:text-white/70 mb-4">
                Quando quiseres, avança para o próximo episódio.
              </p>
              <button
                onClick={() => navigate(`/play/${token}`)}
                className="px-8 py-4 text-lg font-semibold text-white bg-gradient-to-r from-primary to-accent rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200"
              >
                {isResolveEpisode ? 'Iniciar resolução' : 'Próximo episódio'}
              </button>
            </div>
          </div>
        )}

        {/* Email prompt (F4) — pede-se o email no fim do EP2 para sincronização
         * cross-device. Só aparece quando o jogador já completou o EP2, o job
         * assíncrono terminou (currentEpisode > displayEpisodeNumber) e ainda
         * não tem email. "Agora não" grava flag em sessionStorage. */}
        {token
          && !emailAttached
          && !emailPromptDismissed
          && !conflictData
          && displayEpisodeNumber !== undefined
          && displayEpisodeNumber >= EMAIL_PROMPT_AFTER_EPISODE
          && data.currentEpisode > displayEpisodeNumber
          && data.hasEmail === false && (
          <div className="bg-white dark:bg-white/10 dark:backdrop-blur-md dark:border dark:border-white/15 rounded-2xl shadow-sm dark:shadow-none p-6 mb-8">
            <h2 className="text-lg font-semibold text-dark dark:text-white mb-2">
              Sincronizar entre dispositivos
            </h2>
            <p className="text-sm text-gray-600 dark:text-white/70 mb-4">
              Mete o email para continuares noutro dispositivo. Só te enviamos
              novidades do jogo — nunca spam.
            </p>
            <form onSubmit={handleAttachEmail} className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                value={emailPromptEmail}
                onChange={(e) => setEmailPromptEmail(e.target.value)}
                placeholder="o.teu@email.com"
                required
                disabled={isAttachingEmail}
                className="flex-1 px-4 py-3 rounded-xl border border-gray-200 dark:border-white/20 dark:bg-white/5 dark:text-white dark:placeholder-white/40 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all disabled:opacity-50"
              />
              <button
                type="submit"
                disabled={isAttachingEmail || !emailPromptEmail.trim()}
                className="px-6 py-3 font-semibold text-white bg-gradient-to-r from-primary to-accent rounded-xl shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isAttachingEmail ? 'A guardar...' : 'Guardar'}
              </button>
            </form>
            {emailPromptError && (
              <p className="text-red-500 dark:text-red-400 text-sm mt-2">{emailPromptError}</p>
            )}
            <button
              type="button"
              onClick={handleDismissEmailPrompt}
              className="mt-3 text-sm text-gray-400 dark:text-white/40 hover:text-gray-600 dark:hover:text-white/70 hover:underline"
            >
              Agora não
            </button>
          </div>
        )}

        {/* Confirmação leve após attach com sucesso */}
        {emailAttached && (
          <div className="bg-green-50 dark:bg-green-500/10 rounded-2xl p-4 mb-8 text-center">
            <p className="text-green-700 dark:text-green-400 text-sm font-medium">
              ✓ Sincronização ativada. Enviámos um link mágico para o teu email
              para poderes continuar noutro dispositivo.
            </p>
          </div>
        )}

        {/* Modal de conflito (F5) — o email já tem um run noutro device.
         * O jogador escolhe qual run manter. */}
        {conflictData && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-dark/70 backdrop-blur-sm">
            <div className="bg-white dark:bg-dark dark:border dark:border-white/15 rounded-2xl shadow-2xl p-6 md:p-8 max-w-lg w-full">
              <h3 className="text-xl md:text-2xl font-bold text-dark dark:text-white mb-3">
                Tens um jogo guardado neste email
              </h3>
              <p className="text-gray-600 dark:text-white/70 mb-6">
                Encontrámos outra aventura associada a este email. Qual queres
                continuar?
              </p>
              <div className="space-y-3 mb-6">
                {/* Run actual (anónimo, neste device) */}
                <button
                  type="button"
                  onClick={() => handleResolveConflict('current')}
                  disabled={isResolvingConflict}
                  className="w-full text-left p-4 rounded-2xl border-2 border-primary bg-primary/5 dark:bg-primary/15 hover:bg-primary/10 transition-all disabled:opacity-50"
                >
                  <p className="text-xs text-gray-500 dark:text-white/60 mb-1">Neste dispositivo</p>
                  <p className="font-semibold text-dark dark:text-white">{conflictData.current.adventureTitle}</p>
                  <p className="text-sm text-gray-500 dark:text-white/60">
                    Episódio {conflictData.current.currentEpisode} de {conflictData.current.totalEpisodes}
                  </p>
                </button>
                {/* Run existente (do email, noutro device) */}
                <button
                  type="button"
                  onClick={() => handleResolveConflict('existing')}
                  disabled={isResolvingConflict}
                  className="w-full text-left p-4 rounded-2xl border border-gray-200 dark:border-white/15 hover:border-primary/50 transition-all disabled:opacity-50"
                >
                  <p className="text-xs text-gray-500 dark:text-white/60 mb-1">No email</p>
                  <p className="font-semibold text-dark dark:text-white">{conflictData.existing.adventureTitle}</p>
                  <p className="text-sm text-gray-500 dark:text-white/60">
                    Episódio {conflictData.existing.currentEpisode} de {conflictData.existing.totalEpisodes}
                    {conflictData.existing.playerStatus === 'completed' ? ' · Completa' : ''}
                  </p>
                </button>
              </div>
              {conflictError && (
                <p className="text-red-500 dark:text-red-400 text-sm mb-3">{conflictError}</p>
              )}
              <p className="text-xs text-gray-400 dark:text-white/40 text-center">
                {isResolvingConflict ? 'A resolver...' : 'O run não escolhido será arquivado.'}
              </p>
            </div>
          </div>
        )}

        {/* Top action block: choice appears first when available */}
        {isCandidatesReady && token && !isLastEpisode && !isResolveEpisode && data.nextCandidates && data.nextCandidates.length > 0 && (() => {
          const knownAvailable = data.nextCandidates.filter((c: any) => c.isKnownCharacter && !c.isExhausted);
          const knownExhausted = data.nextCandidates.filter((c: any) => c.isKnownCharacter && c.isExhausted);
          const newCandidates = data.nextCandidates.filter((c: any) => !c.isKnownCharacter);

          return (
            <div className="bg-white dark:bg-white/10 dark:backdrop-blur-md dark:border dark:border-white/15 rounded-2xl shadow-sm dark:shadow-none p-6 mb-8">
              <h2 className="text-xl font-semibold text-dark dark:text-white mb-2">
                Escolhe a próxima entrevista
              </h2>

              {/* Known available characters — the detective's board */}
              {knownAvailable.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-500 dark:text-white/60 uppercase tracking-wider mb-3">
                    Pessoas que já conheces
                  </h3>
                  <div className="grid gap-3">
                    {knownAvailable.map((candidate: any) => (
                      <button
                        key={candidate.id}
                        onClick={() => setSelectedCandidateId(candidate.id)}
                        className={`text-left w-full p-4 rounded-2xl border transition-all ${
                          selectedCandidateId === candidate.id
                            ? 'border-primary bg-primary/5 dark:bg-primary/15 shadow-md'
                            : 'border-gray-200 dark:border-white/15 bg-white dark:bg-white/5 hover:border-primary/50'
                        }`}
                      >
                        <span className="inline-block text-xs text-gray-500 dark:text-white/50 mb-2">
                          Conversas: {candidate.interactionCount} de {candidate.interactionLimit}
                        </span>
                        <div className="flex items-start gap-4">
                          <div className="w-14 h-14 shrink-0">
                            <img
                              src={candidate.avatarUrl || getCandidateAvatar(candidate.genderHint)}
                              alt={`Avatar de ${candidate.name}`}
                              className="w-full h-full rounded-xl object-cover border border-gray-200 dark:border-white/20"
                            />
                          </div>
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-dark dark:text-white">
                              {candidate.name}
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-white/60">{candidate.role}</p>
                            {Array.isArray(candidate.openQuestions) && candidate.openQuestions.length > 0 && (
                              <ul className="mt-2 space-y-1">
                                {candidate.openQuestions.slice(0, 2).map((question: string, i: number) => (
                                  <li key={i} className="text-xs text-primary/80 italic">— {question}</li>
                                ))}
                              </ul>
                            )}
                            {candidate.referenceSource && (
                              <p className="text-xs text-gray-500 dark:text-white/50 mt-2">
                                {candidate.referenceSource.includes('episódio') || candidate.referenceSource.includes('episodio')
                                  ? `Referido por: ${candidate.referenceSource}`
                                  : candidate.referenceSource}
                              </p>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* New suggestions — characters not yet met */}
              {newCandidates.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-500 dark:text-white/60 uppercase tracking-wider mb-3">
                    {knownAvailable.length > 0 || knownExhausted.length > 0 ? 'Novas pistas' : 'Quem queres conhecer?'}
                  </h3>
                  <div className="grid gap-3">
                    {newCandidates.map((candidate: any) => (
                      <button
                        key={candidate.id}
                        onClick={() => setSelectedCandidateId(candidate.id)}
                        className={`text-left w-full p-4 rounded-2xl border-2 border-dashed transition-all ${
                          selectedCandidateId === candidate.id
                            ? 'border-primary bg-primary/5 dark:bg-primary/15 shadow-md'
                            : 'border-gray-300 dark:border-white/20 bg-gray-50/50 dark:bg-white/5 hover:border-primary/50'
                        }`}
                      >
                        <span className="inline-block text-xs text-gray-400 dark:text-white/40 italic mb-2">Ainda não conheces</span>
                        <div className="flex items-start gap-4">
                          <div className="w-14 h-14 shrink-0">
                            <img
                              src={getCandidateAvatar(candidate.genderHint)}
                              alt={`Avatar de ${candidate.name}`}
                              className="w-full h-full rounded-xl object-cover border border-gray-300 dark:border-white/20"
                            />
                          </div>
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-dark dark:text-white">
                              {candidate.name}
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-white/60">{candidate.reasonSafe || candidate.role}</p>
                            {Array.isArray(candidate.openQuestions) && candidate.openQuestions.length > 0 && (
                              <ul className="mt-2 space-y-1">
                                {candidate.openQuestions.slice(0, 2).map((question: string, i: number) => (
                                  <li key={i} className="text-xs text-primary/80 italic">— {question}</li>
                                ))}
                              </ul>
                            )}
                            {candidate.referenceSource && (
                              <p className="text-xs text-gray-500 dark:text-white/50 mt-2">
                                {candidate.referenceSource.includes('episódio') || candidate.referenceSource.includes('episodio')
                                  ? `Referido por: ${candidate.referenceSource}`
                                  : candidate.referenceSource}
                              </p>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Exhausted characters — met but no more conversations left */}
              {knownExhausted.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-400 dark:text-white/40 uppercase tracking-wider mb-3">
                    Já não disponíveis
                  </h3>
                  <div className="grid gap-3">
                    {knownExhausted.map((candidate: any) => (
                      <div
                        key={candidate.id}
                        className="text-left w-full p-4 rounded-2xl border border-gray-100 dark:border-white/10 bg-gray-50 dark:bg-white/5 opacity-50"
                      >
                        <span className="inline-block text-xs text-gray-400 dark:text-white/40 italic mb-2">
                          {candidate.interactionLimit} de {candidate.interactionLimit} conversas
                        </span>
                        <div className="flex items-start gap-4">
                          <div className="w-14 h-14 shrink-0">
                            <img
                              src={candidate.avatarUrl || getCandidateAvatar(candidate.genderHint)}
                              alt={`Avatar de ${candidate.name}`}
                              className="w-full h-full rounded-xl object-cover border border-gray-200 dark:border-white/15 grayscale"
                            />
                          </div>
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-gray-400 dark:text-white/40">
                              {candidate.name}
                            </h3>
                            <p className="text-sm text-gray-400 dark:text-white/40">{candidate.role}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {choiceError && (
                <p className="text-red-500 dark:text-red-400 text-sm mt-4">{choiceError}</p>
              )}

              <div className="text-center mt-6">
                <button
                  onClick={() => handleConfirmCandidate()}
                  disabled={isSelectingCandidate}
                  className="px-8 py-4 text-lg font-semibold text-white bg-gradient-to-r from-primary to-accent rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isSelectingCandidate ? 'A preparar...' : 'Confirmar escolha'}
                </button>
              </div>
            </div>
          );
        })()}

        {/* "Ver avaliação" button — shown when final evaluation is ready */}
        {evaluationReady && token && (
          <div className="bg-white dark:bg-white/10 dark:backdrop-blur-md dark:border dark:border-white/15 rounded-2xl shadow-sm dark:shadow-none p-8 mb-8 text-center">
            <div className="text-5xl mb-4">🎉</div>
            <p className="text-gray-700 dark:text-white/80 font-medium mb-6">
              A tua aventura está concluída e a avaliação final está pronta.
            </p>
            <button
              onClick={() => navigate(`/complete/${token}`)}
              className="px-8 py-4 text-lg font-semibold text-white bg-gradient-to-r from-primary to-accent rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200"
            >
              Ver avaliação
            </button>
          </div>
        )}

        {/* Generation Progress — sticky so it stays visible while scrolling the recap */}
        {shouldShowStatusIndicator && (
          <div className="sticky top-0 z-20 -mx-4 px-4 pt-2 pb-4 bg-gradient-to-b from-gray-50 dark:from-dark via-gray-50 dark:via-dark to-transparent">
            <GenerationProgress
              status={data.nextEpisodeStatus}
              generationStep={data.generationStep ?? null}
              generationStepName={data.generationStepName ?? null}
              isResolveEpisode={isResolveEpisode}
              isProcessingFinalEvaluation={isProcessingFinalEvaluation}
              isLastEpisode={isLastEpisode}
            />
          </div>
        )}

        {/* Episode Recap - shown only AFTER the user selects the next character.
         * This gives the user something to read while the episode is being generated,
         * rather than showing it too early when they haven't chosen yet.
         * Exception: resolve/last-episode flows have no character selection, so show immediately. */}
        {displaySummary && (isPreparingSelectedEpisode || isEpisodeReady || evaluationReady || isProcessingFinalEvaluation || isResolveFlow || isLastEpisode) && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-dark dark:text-white mb-4">
              O que aconteceu
            </h2>
            <RecapDisplay
              characterName={displayCharacterName}
              summary={displaySummary}
              npcReaction={displayNpcReaction}
              episodeNumber={displayEpisodeNumber ?? data.currentEpisode}
              isLoadingReaction={isPreparingSelectedEpisode && !displayNpcReaction}
              bridgeText={lockedBridge}
            />
          </div>
        )}

        {isCandidatesReady && token && !isLastEpisode && !isResolveEpisode && (!data.nextCandidates || data.nextCandidates.length === 0) && (
          <div className="bg-red-50 dark:bg-red-500/10 rounded-2xl p-6 text-center">
            <p className="text-red-700 dark:text-red-400">
              Faltam candidatos para escolher o próximo episódio.
            </p>
          </div>
        )}

        {/* Pending State (only if NOT the last episode) */}
        {data.nextEpisodeStatus === 'pending' && !isLastEpisode && !isResolveEpisode && (
          <div className={`${data.generationError ? 'bg-red-50 dark:bg-red-500/10' : 'bg-yellow-50 dark:bg-yellow-500/10'} rounded-2xl p-6 text-center`}>
            {data.generationError ? (
              <>
                <div className="text-4xl mb-3">😕</div>
                <p className="text-red-800 dark:text-red-300 font-medium">
                  Ocorreu um erro a preparar o próximo episódio.
                </p>
                <p className="text-sm text-red-600 dark:text-red-400 mt-2">
                  A nossa equipa já foi notificada e está a resolver o problema. Receberás um email quando o episódio estiver pronto.
                </p>
              </>
            ) : (
              <>
                <p className="text-yellow-800 dark:text-yellow-300">
                  O próximo episódio ainda não foi preparado.
                </p>
                <p className="text-sm text-yellow-600 dark:text-yellow-400 mt-2">
                  Podes fechar esta página — receberás um email quando estiver disponível.
                </p>
              </>
            )}
          </div>
        )}

        {/* Resolve Case CTA — only after player has completed at least half of the interview episodes */}
        {data.allowResolveEarly && isCandidatesReady && !isLastEpisode && !isResolveEpisode &&
         data.totalEpisodes && data.currentEpisode > Math.ceil((data.totalEpisodes - 2) / 2) + 1 && (
          <div className="bg-white dark:bg-white/10 dark:backdrop-blur-md dark:border dark:border-white/15 rounded-2xl shadow-sm dark:shadow-none p-6 text-center">
            <h2 className="text-lg font-semibold text-dark dark:text-white mb-2">
              Iniciar resolução do caso
            </h2>
            <p className="text-sm text-gray-600 dark:text-white/70 mb-4">
              Se achares que já tens pistas suficientes, podes começar a conversa final com a autoridade.
            </p>
            <button
              onClick={handleResolveCase}
              disabled={isResolving}
              className="px-6 py-3 bg-gray-900 dark:bg-white/20 text-white font-medium rounded-xl hover:bg-gray-800 dark:hover:bg-white/30 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isResolving ? 'A preparar...' : 'Iniciar resolução'}
            </button>
            {resolveError && (
              <p className="text-red-500 dark:text-red-400 text-sm mt-3">{resolveError}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
